import { ARTICLE_GROUND } from '../util/article-ground';
import { ARTICLE_DISPLAY_TYPE } from '../util/article-display-type';
import { useEffect, useRef, useState } from 'react';
import { Link, useFetcher } from 'react-router';
import { useLogImpression, useTracker } from '@archipro-website/tracker';
import { cn } from '@rocco/utils/cn';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import {
    DIRECTORY_GUTTER_X,
    EditorialArticleGrid,
} from '~/modules/articles-revamp/component/EditorialArticleGrid';
import { EditorialViewMore } from '~/modules/articles-revamp/component/EditorialViewMore';
import { mapArchizeenTileToEditorial } from '~/modules/articles-revamp/util/map-archizeen-tile-to-editorial';
import type { ArchizeenTile } from '~/modules/articles-revamp/util/map-archizeen-tile-to-editorial';
import { HREF_ARTICLE_TILES } from '~/routes/remix-api.article.get-article-tiles';

interface RevampDiscoverMoreProps {
    tiles: ArticleTileViewModel[];
    /** The article being read, so the rail never offers it back. */
    currentArticleID?: number;
    /**
     * The subject the rail is named for, linked to its own listing. Omitted
     * where an article carries none, and the title then just says "Reading".
     */
    theme?: { title: string; link: string };
}

/** One turn of the grid's rhythm, which is also one page of the rail. */
const PAGE_SIZE = 10;

/**
 * "Discover More Reading" (Figma 2473:15887 + 2456:16466): the giant two-tone
 * heading over the article directory's own grid.
 *
 * The grid is the directory's component rather than a copy of it — same
 * gutters, same 24/100 gaps, same run of doubles and singles, same card. A rail
 * of articles below an article and a page of articles are the same thing, and
 * two implementations of it would drift the first time either changed.
 *
 * Self-hides when empty.
 */
export const RevampDiscoverMore = ({
    tiles,
    currentArticleID,
    theme,
}: RevampDiscoverMoreProps) => {
    const tracker = useTracker();
    const fetcher = useFetcher<ArchizeenTile[]>();
    const [loaded, setLoaded] = useState<ArticleTileViewModel[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    // The last response folded in. `fetcher.data` survives the fetch that
    // follows it, so without this the effect would append the same page again
    // on the next render pass.
    const foldedRef = useRef<unknown>(null);

    useEffect(() => {
        const data = fetcher.data;
        if (fetcher.state !== 'idle' || !data || foldedRef.current === data) {
            return;
        }
        foldedRef.current = data;

        // A short page is the end of the listing.
        if (data.length < PAGE_SIZE) setHasMore(false);

        setLoaded((previous) => {
            const seen = new Set([
                ...(currentArticleID ? [currentArticleID] : []),
                ...tiles.map((tile) => tile.id),
                ...previous.map((tile) => tile.id),
            ]);
            const fresh = data
                .filter((tile) => !seen.has(tile.ID))
                .map((tile) =>
                    mapArchizeenTileToEditorial(tile, {
                        variant: 'editorialLarge',
                    }),
                );
            return fresh.length ? [...previous, ...fresh] : previous;
        });
    }, [currentArticleID, fetcher.data, fetcher.state, tiles]);

    const loadMore = () => {
        const params = new URLSearchParams({
            limit: String(PAGE_SIZE),
            offset: String(page * PAGE_SIZE),
            searchSort: 'latest',
        });
        fetcher.load(`${HREF_ARTICLE_TILES}?${params.toString()}`);
        setPage((current) => current + 1);
    };

    if (tiles.length === 0) return null;

    // The name the legacy footer rail reported, kept because this is that
    // rail: `RelatedArticles.tsx` sat in the article footer and this replaced
    // it. A new name would read as a new event and split the series at the
    // flag flip, so the rail is renamed in the interface only.
    const logClick = (articleID: number) =>
        tracker.log('ArticleRelatedArticlesClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: { ExtraData: articleID },
        });

    return (
        <section className={cn('w-full', ARTICLE_GROUND)}>
            <h2
                // Gestura, the editorial serif the article's own titles are
                // set in — the rail closes the article rather than opening a
                // new page, so it is titled in the article's voice. Weight is
                // normal because Gestura Headline serves one cut; the size and
                // ink stay as the detail pages' section head has them.
                // px-4.5: the article's one phone gutter is the prose's
                // 18px, over the directory's 16 — the grid and Load More
                // below carry the same override.
                className={cn(
                    'm-0',
                    ARTICLE_DISPLAY_TYPE,
                    DIRECTORY_GUTTER_X,
                    'px-4.5',
                )}
            >
                {/* The design's #a7a7a8 is 2.4:1 on white — under the 3:1 large
                    text needs. #8c8c8c keeps the two-tone split at 3.4:1.
                    
                    Naming the subject makes the title a promise the grid can
                    keep, and the subject is a real place to go — so it is a
                    link, not just the coloured half of a heading. */}
                Discover More Reading
                {theme ? (
                    // Broken by construction, not by wrapping: the subject
                    // belongs on its own line whatever its length, and a long
                    // one would otherwise leave "in" stranded above it.
                    <span className="block">
                        in{' '}
                        <Link
                            to={theme.link}
                            prefetch="intent"
                            className="text-[#8c8c8c] no-underline transition-colors hover:text-[#323232]"
                        >
                            {theme.title}
                        </Link>
                    </span>
                ) : null}
            </h2>
            <EditorialArticleGrid
                articles={[...tiles, ...loaded]}
                layout="directory"
                className="px-4.5 max-md:mt-8 md:mt-[3.75rem]"
                // The rail's own instrumentation, kept through the swap to the
                // shared grid: an `Impression` per card (MRC standard, via the
                // overlay so each card gets its own observed element) and the
                // one merged click event the revamp logs for this rail.
                renderOverlay={(article) => (
                    <DiscoverMoreImpression article={article} />
                )}
                onTileClick={(article) => logClick(article.id)}
            />
            {/* The directory's own control: an explicit Load More first, and
                infinite scroll from the first click onwards. */}
            <EditorialViewMore
                hasMore={hasMore}
                isLoading={fetcher.state !== 'idle'}
                onLoadMore={loadMore}
                nextPage={page + 1}
                className={cn('mt-[3.75rem]', DIRECTORY_GUTTER_X, 'px-4.5')}
            />
        </section>
    );
};

/**
 * Logs one impression for a card in the rail.
 *
 * Mounted as the card's overlay because that is the one place the shared grid
 * gives a caller per-card: the tile itself is rocco's and takes no ref.
 */
const DiscoverMoreImpression = ({
    article,
}: {
    article: ArticleTileViewModel;
}) => {
    const markerRef = useRef<HTMLSpanElement>(null);

    useLogImpression({
        ref: markerRef,
        data: {
            itemID: article.id,
            professionalID: article.professional?.id ?? 0,
            type: 'DiscoverMore',
        },
    });

    return (
        <span
            ref={markerRef}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
        />
    );
};
